<template>
  <v-container fluid class="pa-4">
    <v-row class="mb-6">
      <v-col cols="12">
        <h1 class="app-page-title page-title">Contact information</h1>
        <p class="page-subtitle mb-0">
          Keep your mailing address and phone number up to date so we can reach you about your plan and payments.
        </p>
      </v-col>
    </v-row>

    <v-row align="start">
      <v-col cols="12" lg="7" xl="6">
        <v-card
          class="member-contact-card rounded-lg"
          elevation="0"
          :loading="loading && !loadError"
        >
          <div class="member-contact-card__toolbar pa-5 pb-0">
            <h2 class="member-contact-card__heading">
              <v-icon color="primary" class="mr-2" size="22">mdi-account-edit</v-icon>
              Your details on file
            </h2>
            <p class="member-contact-card__lede caption mb-0">
              Changes apply to the subscriber contact tied to your enrollment.
            </p>
          </div>

          <v-card-text class="pt-4 px-5 pb-5">
            <div v-if="loading && !loadError" class="member-contact-skeleton py-10 text-center">
              <v-progress-circular indeterminate color="primary" size="48" width="3" />
              <div class="caption grey--text mt-4">Loading your information…</div>
            </div>

            <v-alert
              v-else-if="loadError"
              type="error"
              outlined
              prominent
              border="left"
              colored-border
              class="member-contact-alert mb-0"
            >
              <div class="font-weight-medium">We couldn’t load your contact information</div>
              <div class="body-2 mt-1">{{ loadError }}</div>
            </v-alert>

            <template v-else>
              <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="save">
                <section class="member-contact-section">
                  <div class="member-contact-section__label text-overline">
                    Mailing address
                  </div>
                  <v-text-field
                    v-model="form.addressLine1"
                    label="Street address"
                    outlined
                    dense
                    hide-details="auto"
                    prepend-inner-icon="mdi-map-marker-outline"
                    :rules="[rules.required, rules.max200]"
                    autocomplete="street-address"
                    class="mb-3"
                  />
                  <v-text-field
                    v-model="form.addressLine2"
                    label="Apt, suite, unit (optional)"
                    outlined
                    dense
                    hide-details="auto"
                    prepend-inner-icon="mdi-office-building-outline"
                    :rules="[rules.max200]"
                    autocomplete="address-line2"
                    class="mb-2"
                  />
                  <v-row dense class="mt-0">
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="form.city"
                        label="City"
                        outlined
                        dense
                        hide-details="auto"
                        prepend-inner-icon="mdi-city"
                        :rules="[rules.required, rules.max100]"
                        autocomplete="address-level2"
                      />
                    </v-col>
                    <v-col cols="6" sm="3">
                      <v-text-field
                        v-model="form.state"
                        label="State"
                        outlined
                        dense
                        hide-details="auto"
                        maxlength="2"
                        hint="e.g. FL"
                        prepend-inner-icon="mdi-map-outline"
                        persistent-hint
                        :rules="[rules.state]"
                        autocomplete="address-level1"
                        class="text-uppercase"
                        @input="form.state = (form.state || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2)"
                      />
                    </v-col>
                    <v-col cols="6" sm="3">
                      <v-text-field
                        v-model="form.zip"
                        label="ZIP"
                        outlined
                        dense
                        hide-details="auto"
                        prepend-inner-icon="mdi-mailbox-outline"
                        :rules="[rules.required, rules.maxZip]"
                        autocomplete="postal-code"
                      />
                    </v-col>
                  </v-row>
                </section>

                <v-divider class="my-6 member-contact-divider" />

                <section class="member-contact-section">
                  <div class="member-contact-section__label text-overline">
                    Phone
                  </div>
                  <v-text-field
                    v-model="form.phone"
                    label="Phone number"
                    outlined
                    dense
                    hide-details="auto"
                    prepend-inner-icon="mdi-phone-outline"
                    :rules="[rules.required, rules.maxPhone]"
                    autocomplete="tel"
                    class="mb-0"
                  />
                </section>

                <v-alert
                  v-if="saveError"
                  type="error"
                  outlined
                  dense
                  class="mt-6 mb-0 member-contact-inline-alert"
                >
                  {{ saveError }}
                </v-alert>
                <v-alert
                  v-if="saveSuccess"
                  type="success"
                  outlined
                  dense
                  class="mt-6 mb-0 member-contact-inline-alert"
                >
                  {{ saveSuccess }}
                </v-alert>

                <div class="member-contact-actions d-flex flex-wrap align-center mt-8">
                  <v-btn
                    type="submit"
                    color="primary"
                    depressed
                    large
                    class="member-contact-btn-primary px-6 text-none font-weight-semibold"
                    :loading="saving"
                    :disabled="saving"
                  >
                    <v-icon left small class="mr-1">mdi-content-save-outline</v-icon>
                    Save changes
                  </v-btn>
                  <v-btn
                    outlined
                    color="primary"
                    large
                    class="member-contact-btn-secondary ml-sm-3 mt-3 mt-sm-0 text-none"
                    :disabled="saving"
                    @click="loadContact"
                  >
                    <v-icon left small class="mr-1">mdi-restore</v-icon>
                    Discard changes
                  </v-btn>
                </div>
              </v-form>
            </template>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" lg="5" xl="5">
        <aside class="member-contact-aside pa-0">
          <v-sheet
            rounded="lg"
            class="member-contact-tip pa-5"
            elevation="0"
          >
            <div class="d-flex align-start">
              <v-icon color="primary" class="mr-3 mt-1">mdi-information-outline</v-icon>
              <div>
                <div class="subtitle-2 font-weight-bold mb-2">Why this matters</div>
                <ul class="member-contact-tip__list body-2 mb-0 pl-0">
                  <li>Payment receipts and plan notices use this address and phone.</li>
                  <li>Updating here keeps your member profile aligned with billing.</li>
                </ul>
              </div>
            </div>
          </v-sheet>
        </aside>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  name: 'MemberContactInfo',
  data() {
    return {
      loading: true,
      saving: false,
      valid: false,
      loadError: '',
      saveError: '',
      saveSuccess: '',
      form: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
      },
      rules: {
        required: (v) => !!(v && String(v).trim()) || 'This field is required',
        max200: (v) => !v || String(v).length <= 200 || 'Max 200 characters',
        max100: (v) => !v || String(v).length <= 100 || 'Max 100 characters',
        maxZip: (v) => !v || String(v).length <= 20 || 'Max 20 characters',
        maxPhone: (v) => !v || String(v).length <= 40 || 'Max 40 characters',
        state: (v) =>
          !!(v && String(v).trim().length === 2) || 'Use a 2-letter state code',
      },
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      try {
        const ures = await axios.get('/api/user');
        const u = ures.data && ures.data[0];
        if (!u || String(u.role).toLowerCase() !== 'member') {
          this.$router.push('/');
          return;
        }
        await this.loadContact();
      } catch {
        this.$router.push('/login');
      }
    },
    loadContact() {
      this.loading = true;
      this.loadError = '';
      this.saveError = '';
      this.saveSuccess = '';
      return axios
        .get('/api/enrollment/member-contact')
        .then((res) => {
          if (res.data && res.data.success) {
            this.form = {
              addressLine1: res.data.addressLine1 || '',
              addressLine2: res.data.addressLine2 || '',
              city: res.data.city || '',
              state: (res.data.state || '').toUpperCase(),
              zip: res.data.zip || '',
              phone: res.data.phone || '',
            };
          }
        })
        .catch((err) => {
          const msg =
            (err.response && err.response.data && err.response.data.error) ||
            err.message ||
            'Unable to load contact information';
          this.loadError = msg;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    save() {
      this.saveError = '';
      this.saveSuccess = '';
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      axios
        .put('/api/enrollment/member-contact', { ...this.form })
        .then((res) => {
          if (res.data && res.data.success) {
            this.saveSuccess = res.data.message || 'Saved successfully.';
            this.$root.$emit('refresh-member-billing-alert');
          }
        })
        .catch((err) => {
          const msg =
            (err.response && err.response.data && err.response.data.error) ||
            err.message ||
            'Could not save';
          this.saveError = msg;
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
$contact-teal: #007d82;
$contact-teal-dark: #00adb5;

.member-contact-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow:
    0 2px 12px rgba(0, 0, 0, 0.06),
    0 0 0 1px rgba(0, 125, 130, 0.06) !important;
}

.theme--dark .member-contact-card {
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25) !important;
}

.member-contact-card__heading {
  display: flex;
  align-items: center;
  font-size: 1.125rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
  letter-spacing: -0.01em;
}

.theme--dark .member-contact-card__heading {
  color: rgba(255, 255, 255, 0.95);
}

.member-contact-card__lede {
  margin-top: 8px;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.5;
}

.theme--dark .member-contact-card__lede {
  color: rgba(255, 255, 255, 0.6);
}

.member-contact-section__label {
  color: $contact-teal;
  font-weight: 700;
  letter-spacing: 0.08em !important;
  margin-bottom: 14px;
  opacity: 0.9;
}

.theme--dark .member-contact-section__label {
  color: $contact-teal-dark;
}

.member-contact-divider {
  border-color: rgba(0, 125, 130, 0.12) !important;
}

.theme--dark .member-contact-divider {
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.member-contact-tip {
  border: 1px solid rgba(0, 125, 130, 0.2);
  background: linear-gradient(180deg, rgba(0, 125, 130, 0.06) 0%, rgba(0, 125, 130, 0.02) 100%);
}

.theme--dark .member-contact-tip {
  border-color: rgba(0, 173, 181, 0.25);
  background: linear-gradient(180deg, rgba(0, 173, 181, 0.1) 0%, rgba(0, 173, 181, 0.03) 100%);
}

.member-contact-tip__list {
  list-style: none;

  li {
    position: relative;
    padding-left: 14px;
    margin-bottom: 10px;
    color: rgba(0, 0, 0, 0.68);
    line-height: 1.5;

    &:last-child {
      margin-bottom: 0;
    }

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.55em;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: $contact-teal;
    }
  }
}

.theme--dark .member-contact-tip__list li {
  color: rgba(255, 255, 255, 0.72);

  &::before {
    background: $contact-teal-dark;
  }
}

.member-contact-btn-primary {
  letter-spacing: 0.02em;
  box-shadow: 0 3px 12px rgba(0, 125, 130, 0.35) !important;
}

.theme--dark .member-contact-btn-primary {
  box-shadow: 0 3px 14px rgba(0, 173, 181, 0.25) !important;
}

.font-weight-semibold {
  font-weight: 600 !important;
}

.member-contact-inline-alert {
  border-radius: 10px !important;
}

.member-contact-alert {
  border-radius: 10px !important;
}
</style>
